// Master design system — Hack2skill (template1-aligned: primary #306bff)
// Semantic success/warning/error stay distinct for accessibility

const baseColors = {
  white: '#ffffff',
  black: '#0f172a',
  grey: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
  },
  /** Brand blue (Hack2skill / template1 CTA) */
  brand: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#5b8cff',
    600: '#306bff',
    700: '#2557d4',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },
};

const rolePalettes = {
  admin: {
    primary: {
      main: '#306bff',
      light: '#5b8cff',
      dark: '#2557d4',
      darker: '#1e3a8a',
      darkest: '#0f2744',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5b8cff',
      light: '#93c5fd',
      dark: '#306bff',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
      elevated: '#ffffff',
      dark: '#0f2744',
      darkSecondary: '#1e3a8a',
    },
    success: {
      main: '#059669',
      light: '#10b981',
      dark: '#047857',
      contrastText: '#ffffff',
    },
    info: {
      main: '#0284c7',
      light: '#0ea5e9',
      dark: '#0369a1',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#ffffff',
    },
    error: {
      main: '#dc2626',
      light: '#ef4444',
      dark: '#b91c1c',
      contrastText: '#ffffff',
    },
  },
  staff: {
    primary: {
      main: '#306bff',
      light: '#5b8cff',
      dark: '#2557d4',
      darker: '#1e3a8a',
      darkest: '#0f2744',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#5b8cff',
      light: '#93c5fd',
      dark: '#306bff',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
      elevated: '#ffffff',
      dark: '#0f2744',
      darkSecondary: '#1e3a8a',
    },
    success: {
      main: '#059669',
      light: '#10b981',
      dark: '#047857',
      contrastText: '#ffffff',
    },
    info: {
      main: '#0284c7',
      light: '#0ea5e9',
      dark: '#0369a1',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
      contrastText: '#ffffff',
    },
    error: {
      main: '#dc2626',
      light: '#ef4444',
      dark: '#b91c1c',
      contrastText: '#ffffff',
    },
  },
};

const spacing = 4;
const spacingScale = {
  1: '4px',
  2: '8px',
  4: '16px',
  8: '32px',
  16: '64px',
};

const radius = 4;
const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
};

const shadow = {
  0: 'none',
  1: '0 0 0 1px rgba(0, 0, 0, 0.05)',
  2: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  3: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  4: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  5: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

const typography = {
  fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  displayXL: { fontSize: '4.5rem', fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.02em' },
  h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.015em' },
  h2: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.01em' },
  h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.4 },
  h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
  h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
  bodyLarge: { fontSize: '1.25rem', fontWeight: 400, lineHeight: 1.6 },
  body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.5 },
  body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.5 },
  bodySmall: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
  button: { fontSize: '0.875rem', fontWeight: 700, lineHeight: 1.4, letterSpacing: '0.01em', textTransform: 'none' },
  caption: { fontSize: '0.75rem', fontWeight: 400, lineHeight: 1.4 },
  overline: { fontSize: '0.625rem', fontWeight: 800, lineHeight: 1.2, letterSpacing: '0.2em', textTransform: 'uppercase' },
};

const componentTokens = {
  card: {
    borderRadius: borderRadius.lg,
    boxShadow: shadow[2],
    padding: spacingScale[8],
    hoverShadow: shadow[3],
  },
  statCard: {
    borderRadius: borderRadius.lg,
    boxShadow: shadow[2],
    padding: spacingScale[8],
    iconSize: 48,
    iconBg: 'primary.main',
    valueFont: 'h3',
    labelFont: 'body2',
    hoverShadow: shadow[3],
  },
  quickAction: {
    borderRadius: borderRadius.lg,
    boxShadow: shadow[2],
    padding: spacingScale[8],
    iconSize: 56,
    iconBg: 'primary.main',
    hoverShadow: shadow[3],
  },
  button: {
    borderRadius: borderRadius.md,
    padding: `${spacingScale[2]} ${spacingScale[4]}`,
    fontSize: typography.button.fontSize,
    fontWeight: typography.button.fontWeight,
    textTransform: typography.button.textTransform,
    boxShadow: shadow[2],
    hoverShadow: shadow[3],
  },
  input: {
    borderRadius: borderRadius.md,
    border: `1px solid ${baseColors.grey[200]}`,
    padding: `${spacingScale[2]} ${spacingScale[4]}`,
    fontSize: typography.body1.fontSize,
    focusBorder: 'primary.main',
    focusRing: '0 0 0 2px rgba(48, 107, 255, 0.12)',
  },
  progressBar: {
    height: 8,
    borderRadius: borderRadius.sm,
  },
  table: {
    borderRadius: borderRadius.lg,
    boxShadow: shadow[2],
    headerBg: baseColors.grey[50],
    rowHoverBg: baseColors.grey[50],
  },
};

const breakpoints = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
};

const motion = {
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
    entrance: '800ms',
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  },
};

const theme = {
  baseColors,
  rolePalettes,
  spacing,
  spacingScale,
  radius,
  borderRadius,
  shadow,
  typography,
  componentTokens,
  breakpoints,
  motion,
};

export default theme;
